import { Layout } from "../components/Layout";
import { ContactForm } from "../components/ContactForm";
import { SectionHeader } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { useSeo } from "../hooks/useSeo";
import { WHATSAPP_LINK, EMAIL, PHONE_DISPLAY } from "../lib/api";
import { MessageCircle, Mail, Phone, MapPin } from "lucide-react";

const Contact = () => {
  useSeo({
    title: "Contact — Christophe Battras, Consultant IA Genève",
    description: "Une question sur l'automatisation de votre entreprise ? Écrivez-moi ou réservez un appel de 20 minutes. Réponse garantie sous 24h.",
    path: "/contact",
  });

  return (
    <Layout>
      <section className="px-6 pt-14 pb-10 md:pt-24 text-center">
        <div className="max-w-3xl mx-auto fade-in">
          <span className="pill-badge mb-6">Contact</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold title-text leading-[1.05] tracking-tight">
            Parlons de votre entreprise — <span className="accent-text">réponse sous 24h</span>
          </h1>
          <p className="body-text text-lg mt-7 leading-relaxed max-w-2xl mx-auto">
            Une question, un projet, un doute ? Écrivez-moi via le formulaire, par email
            ou directement sur WhatsApp.
          </p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <ContactForm source="contact" page="/contact" buttonLabel="Envoyer le message" />

          <div className="space-y-4">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="card-base flex items-start gap-4 hover:translate-y-[-4px] transition-transform"
              style={{ background: "linear-gradient(135deg,#3ecf8e 0%,#2db97a 100%)" }}
            >
              <span className="w-12 h-12 shrink-0 rounded-xl bg-white/20 flex items-center justify-center text-white">
                <MessageCircle size={22} />
              </span>
              <div>
                <p className="text-white text-xs font-semibold uppercase tracking-wider opacity-80">
                  Le plus rapide
                </p>
                <p className="text-white text-xl font-extrabold mt-1">WhatsApp</p>
                <p className="text-white/90 text-sm mt-1">Réponse immédiate, soir et week-end compris.</p>
              </div>
            </a>

            <a href={`mailto:${EMAIL}`} className="card-base flex items-start gap-4" data-testid="contact-email">
              <span className="icon-square shrink-0">
                <Mail size={22} />
              </span>
              <div>
                <p className="title-text text-xs font-semibold uppercase tracking-wider opacity-60">Email</p>
                <p className="title-text text-lg font-bold mt-1">{EMAIL}</p>
                <p className="body-text text-sm mt-1">Réponse sous 24h ouvrées.</p>
              </div>
            </a>

            <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} className="card-base flex items-start gap-4" data-testid="contact-phone">
              <span className="icon-square shrink-0">
                <Phone size={22} />
              </span>
              <div>
                <p className="title-text text-xs font-semibold uppercase tracking-wider opacity-60">Téléphone</p>
                <p className="title-text text-lg font-bold mt-1">{PHONE_DISPLAY}</p>
                <p className="body-text text-sm mt-1">Du lundi au vendredi, 9h–18h.</p>
              </div>
            </a>

            <div className="card-base flex items-start gap-4">
              <span className="icon-square shrink-0">
                <MapPin size={22} />
              </span>
              <div>
                <p className="title-text text-xs font-semibold uppercase tracking-wider opacity-60">Zone d'intervention</p>
                <p className="title-text text-lg font-bold mt-1">Genève & région lémanique</p>
                <p className="body-text text-sm mt-1">Suisse romande · rendez-vous sur site ou visio.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SeoText>
        <h2 className="section-title mb-6">
          Échangeons sur votre projet <span className="accent-text">d'automatisation</span>
        </h2>
        <p className="body-text leading-relaxed mb-5">
          Quelle que soit la nature de votre demande — découverte d'une solution IA,
          devis pour un projet précis, question technique sur l'intégration avec vos
          outils actuels, ou simplement curiosité — je réponds à tous les messages
          personnellement, sans intermédiaire et sans système de tickets. Si la
          conversation peut se régler en 5 minutes par WhatsApp, on le fait par WhatsApp.
          Si elle mérite un appel ou une visio, je vous propose un créneau directement.
        </p>
        <p className="body-text leading-relaxed mb-5">
          La meilleure manière de démarrer reste l'<strong>audit IA gratuit</strong> de 60
          minutes : on prend le temps de cartographier vos processus actuels, j'identifie
          les trois automatisations à plus fort ROI et je vous remets un plan d'action
          écrit que vous gardez — que vous décidiez ou non de travailler avec moi.
          C'est sans engagement, limité à 4 audits par mois, et la majorité des dirigeants
          qui le font repartent avec au moins une idée immédiatement applicable.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Je travaille principalement avec des PME genevoises, lausannoises et vaudoises,
          mais je couvre toute la Suisse romande : Nyon, Morges, Vevey, Montreux, Sion,
          Fribourg, Neuchâtel, La Chaux-de-Fonds. Les rendez-vous se font en présentiel
          à Genève pour les clients de l'arc lémanique, ou en visioconférence pour le
          reste de la Romandie.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Mon engagement est simple : je vous réponds sous 24 heures ouvrées, je ne
          vends rien qui ne soit pas déjà testé dans mes propres entreprises, et je
          garantis un délai maximum de 30 jours entre la signature et la mise en
          production. Si l'IA n'est pas la bonne solution pour votre besoin, je vous le
          dirai franchement et je vous orienterai vers une alternative.
        </p>
        <p className="body-text leading-relaxed">
          Vos données restent confidentielles, hébergées en Suisse, et conformes aux
          réglementations LPD et FADP. Aucune information transmise via ce formulaire
          n'est utilisée à d'autres fins que la réponse à votre demande.
        </p>
      </SeoText>
    </Layout>
  );
};

export default Contact;
